import { apiRequest } from "../auth/api";

export type ParentType = "sire" | "dam";
export type PedigreeSourceType =
  | "owner_statement"
  | "registry_document"
  | "verified_database"
  | "import"
  | "moderator"
  | "legacy";

export interface PedigreeNode {
  occurrenceId: string;
  path: string;
  generation: number;
  slot: number;
  parentType: ParentType | null;
  missing: boolean;
  dog: {
    id: string;
    slug: string;
    registeredName: string;
    sex: "male" | "female" | "unknown";
    breedName: string | null;
    colorName: string | null;
    birthYear: number | null;
    thumbnailUrl: string | null;
    titles: Array<{ code: string; name: string }>;
    verified: boolean;
    version: number;
  } | null;
  relationship: {
    id: string;
    parentType: ParentType;
    version: number;
    sourceType: PedigreeSourceType;
    sourceSummary: string;
    sourceUrl: string | null;
    verificationStatus: "unverified" | "pending" | "verified" | "rejected";
    reviewedBy: { id: string; displayName: string } | null;
    reviewedAt: string | null;
  } | null;
}

export interface PedigreeDocument {
  rootDogId: string;
  rootSlug: string;
  rootVersion: number;
  generations: 3 | 4 | 5;
  nodes: PedigreeNode[];
  siblings: Array<{
    id: string;
    slug: string;
    registeredName: string;
    sex: "male" | "female" | "unknown";
    birthYear: number | null;
    relation?: "full" | "half";
  }>;
  offspring: Array<{
    id: string;
    slug: string;
    registeredName: string;
    sex: "male" | "female" | "unknown";
    birthYear: number | null;
  }>;
  litters: Array<{
    id: string;
    slug: string;
    name: string;
    bornAt: string | null;
    status: string;
    mate: { id: string; slug: string; registeredName: string } | null;
  }>;
  queryPlan: {
    statementCount: 4;
    maxOccurrenceCount: number;
    returnedOccurrenceCount: number;
    elapsedMs: number;
  };
}

export interface PedigreeChangeRequest {
  id: string;
  dogId: string;
  dogSlug: string;
  dogName: string;
  relationshipType: ParentType;
  proposedParent: {
    id: string;
    slug: string;
    registeredName: string;
    sex: "male" | "female" | "unknown";
    dateOfBirth: string | null;
  } | null;
  sourceType: PedigreeSourceType;
  sourceSummary: string;
  sourceUrl: string | null;
  sourceDocumentId: string | null;
  status: "pending" | "approved" | "rejected" | "cancelled";
  baseDogVersion: number;
  validationWarnings: string[];
  requestedBy: { id: string; displayName: string };
  reviewedBy: { id: string; displayName: string } | null;
  reviewerNote: string | null;
  sexOverrideReason: string | null;
  timelineOverrideReason: string | null;
  overrideEvidenceDocumentId: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface PedigreeSourceDocument {
  id: string;
  originalFilename: string;
  mimeType: string;
  byteSize: number;
  sha256: string;
  verificationStatus: "pending";
}

export async function uploadPedigreeSourceDocument(
  dogId: string,
  file: File,
): Promise<{ document: PedigreeSourceDocument }> {
  const form = new FormData();
  form.set("file", file);
  return apiRequest(`/api/v1/pedigree/member/dogs/${encodeURIComponent(dogId)}/documents`, {
    method: "POST",
    body: form,
  });
}

export function pedigreeSourceDocumentUrl(documentId: string): string {
  return `/api/v1/pedigree/documents/${encodeURIComponent(documentId)}/content`;
}

export function getPublicPedigree(slug: string, generations: 3 | 4 | 5) {
  return apiRequest<{ pedigree: PedigreeDocument }>(
    `/api/v1/pedigree/dogs/${encodeURIComponent(slug)}?generations=${generations}`,
  );
}

export function getMemberPedigree(dogId: string, generations: 3 | 4 | 5) {
  return apiRequest<{
    pedigree: PedigreeDocument;
    requests: PedigreeChangeRequest[];
    viewer: { canRequestChange: boolean; canModerate: boolean };
  }>(`/api/v1/pedigree/member/dogs/${encodeURIComponent(dogId)}?generations=${generations}`);
}

export function createPedigreeRequest(
  dogId: string,
  input: {
    relationshipType: ParentType;
    proposedParentDogId: string | null;
    sourceType: "owner_statement" | "registry_document" | "verified_database";
    sourceSummary: string;
    sourceUrl: string | null;
    sourceDocumentId: string | null;
    expectedDogVersion: number;
  },
) {
  return apiRequest<{ request: PedigreeChangeRequest }>(
    `/api/v1/pedigree/member/dogs/${encodeURIComponent(dogId)}/requests`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export function cancelPedigreeRequest(requestId: string) {
  return apiRequest<{ request: PedigreeChangeRequest }>(
    `/api/v1/pedigree/member/requests/${encodeURIComponent(requestId)}/cancel`,
    { method: "POST", body: "{}" },
  );
}

export function listPedigreeModerationQueue() {
  return apiRequest<{ requests: PedigreeChangeRequest[] }>(
    "/api/v1/pedigree/moderation/requests",
  );
}

export function reviewPedigreeRequest(
  requestId: string,
  input: {
    action: "approve" | "reject";
    reviewerNote: string | null;
    sexOverrideReason?: string | null;
    timelineOverrideReason?: string | null;
    overrideEvidenceDocumentId?: string | null;
  },
) {
  return apiRequest<{ request: PedigreeChangeRequest }>(
    `/api/v1/pedigree/moderation/requests/${encodeURIComponent(requestId)}/review`,
    { method: "POST", body: JSON.stringify(input) },
  );
}
